<template>
  <div>
    <div class="info-block px-3 py-1 mb-10 d-flex align-center">
      <v-icon size="24" icon="$warning" />
      <span class="subtitle2 ml-2" v-html="$t('modals.exportActivityLogs.disclaimer')" />
    </div>
    <div class="subtitle1 mb-1">
      {{ $t("modals.exportActivityLogs.subtitle") }}
    </div>
    <v-radio-group v-model="localValue.type" hide-details>
      <v-radio
        v-for="choice in [ExportChoice.ALL, ExportChoice.SPECIFIC]"
        :key="choice"
        class="mb-3"
        :value="choice"
        :ripple="false"
        :label="$t(`modals.exportActivityLogs.choices.${choice}`)"
      ></v-radio>
    </v-radio-group>
    <cui-filter-wrapper :render-clear-button="false" class="mt-2">
      <div class="d-flex flex-column">
        <activity-log-types-filter
          v-model="localValue.filters.types"
          :disabled="localValue.type === ExportChoice.ALL"
          class="filter mb-2"
        />
        <date-range-picker
          v-model="localValue.filters.timeFrame"
          :disabled="localValue.type === ExportChoice.ALL"
          :preset-dates="presets"
          :placeholder="$t('activityLogs.filters.dateRange')"
          class="mr-2 filter"
          :teleport="true"
          hide-menu-arrow
          @click.stop.prevent
        ></date-range-picker>
      </div>
    </cui-filter-wrapper>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { type ExportDialogPayload } from "@/_store/exports.module";
import DateRangePicker from "@/components/DateRangePicker.vue";
import moment from "moment/moment";
import type { ActivityLogsFilters } from "@/_store/activity-logs.module";
import { defaultFiltersState } from "@/_store/filters.module";
import { CuiFilterWrapper } from "@coronet/cui";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useI18n } from "vue-i18n";
import { ExportChoice } from "@/constants/exports.ts";
import ActivityLogTypesFilter from "@/components/filters/ActivityLogTypesFilter.vue";

export default defineComponent({
  components: { CuiFilterWrapper, DateRangePicker, ActivityLogTypesFilter },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();

    const presets = computed(() => [
      {
        label: i18n.t("dateRangePicker.today"),
        value: [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.yesterday"),
        value: [
          moment().subtract(1, "days").format("YYYY-MM-DD"),
          moment().subtract(1, "days").format("YYYY-MM-DD"),
        ],
      },
      {
        label: i18n.t("dateRangePicker.lastSevenDays"),
        value: [moment().subtract(7, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.lastThirtyDays"),
        value: [moment().subtract(30, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.lastNinetyDays"),
        value: [moment().subtract(90, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
    ]);

    const localValue = ref<ExportDialogPayload<ActivityLogsFilters>>({
      type: ExportChoice.ALL,
      filters: {
        ...defaultFiltersState.activityLogsFilters,
      },
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    return {
      localValue,
      ExportChoice,
      presets,
    };
  },
});
</script>

<style lang="scss" scoped>
.filter {
  width: 240px !important;
}

:deep(*) {
  .clear-btn {
    display: none !important;
  }
}
</style>
